import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress
} from '@mui/material';
import api, { inspectionTypesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShareIcon from '@mui/icons-material/Share';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TerrainIcon from '@mui/icons-material/Terrain';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

// Cycle options based on inspection frequency
const getCycleOptions = (frequency) => {
  if (!frequency) return [];
  const map = {
    weekly: { max: 52, name: 'Week' },
    monthly: { max: 12, name: 'Month' },
    quarterly: { max: 4, name: 'Quarter' },
    biannual: { max: 2, name: 'Half-Year' },
    annual: { max: 1, name: 'Year' },
    'ad-hoc': { max: 1, name: 'Ad-hoc' },
    bimonthly: { max: 6, name: 'Period' },
    every_4_months: { max: 3, name: 'Period' },
    every_18_months: { max: 1, name: 'Period' },
    biennial: { max: 1, name: 'Period' },
  };
  const config = map[frequency.toLowerCase()] || { max: 1, name: 'Cycle' };
  return Array.from({ length: config.max }, (_, i) => ({
    value: i + 1,
    label: `${config.name} ${i + 1}`,
  }));
};

// Status display config
const statusConfig = {
  draft: { label: 'Draft', color: '#ff9800' },
  pending_analyze: { label: 'Pending Analyze', color: '#2196f3' },
  in_progress: { label: 'In Progress', color: '#9c27b0' },
  rated: { label: 'Rated', color: '#4caf50' },
};

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

const SlopeReportsPage = () => {
  const theme = useTheme();
  const { getUserOrganization } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [stats, setStats] = useState({
    totalReports: 0,
    latestReportDate: null,
    loading: true
  });

  // Coverage Tracker state
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [coverageData, setCoverageData] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // Fetch reports from backend
  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    try {
      const response = await api.get('/reports', {
        params: {
          report_type: 'inspection_report',
          asset_type: 'Slope',
          limit: 100
        }
      });
      console.log('Fetched slope reports:', response.data);
      setReports(response.data.data || []);

      // Update stats
      const reportData = response.data.data || [];
      setStats({
        totalReports: reportData.length,
        latestReportDate: reportData.length > 0 ? reportData[0].created_at : null,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching slope reports:', error);
      setReports([]);
      setStats({
        totalReports: 0,
        latestReportDate: null,
        loading: false
      });
    } finally {
      setLoadingReports(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Fetch inspection types for Slope asset type on mount
  useEffect(() => {
    const fetchInspectionTypes = async () => {
      try {
        const orgId = getUserOrganization()?.id;
        if (!orgId) return;

        // Get Slope asset_type_id
        const assetTypesRes = await api.get('/asset-types');
        const slopeType = (assetTypesRes.data || []).find(t => t.name === 'Slope');
        if (!slopeType) return;

        const res = await inspectionTypesApi.getTypes({
          organization_id: orgId,
          asset_type_id: slopeType.id,
        });
        setInspectionTypes(res.data || []);
      } catch (error) {
        console.error('Error fetching inspection types:', error);
      }
    };
    fetchInspectionTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch coverage data when all three selections are made
  useEffect(() => {
    if (!selectedTypeId || !selectedCycle || !selectedYear) {
      setCoverageData(null);
      return;
    }
    const fetchCoverage = async () => {
      setCoverageLoading(true);
      try {
        const res = await inspectionTypesApi.getCoverageSummary({
          inspection_type_id: selectedTypeId,
          cycle: selectedCycle,
          year: selectedYear,
        });
        setCoverageData(res.data);
      } catch (error) {
        console.error('Error fetching coverage summary:', error);
        setCoverageData(null);
      } finally {
        setCoverageLoading(false);
      }
    };
    fetchCoverage();
  }, [selectedTypeId, selectedCycle, selectedYear]);

  // Filter reports based on search
  const filteredReports = reports.filter(report => {
    return report.report_name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    setSelectedTypeId(typeId);
    setSelectedCycle('');
    const type = inspectionTypes.find(t => t.id === typeId);
    setSelectedType(type || null);
  };

  // View report in new tab
  const handleViewReport = async (report) => {
    try {
      const response = await api.get(`/reports/${report.id}/download`);
      if (response.data.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        throw new Error(response.data.error || 'Failed to get download link.');
      }
    } catch (err) {
      console.error('Error viewing report:', err);
    }
  };

  // Download report to disk
  const handleDownloadReport = async (report) => {
    try {
      const response = await api.get(`/reports/${report.id}/download`);
      if (response.data.success && response.data.url) {
        const link = document.createElement('a');
        link.href = response.data.url;
        link.setAttribute('download', response.data.fileName || `${report.report_name}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(response.data.error || 'Failed to get download link.');
      }
    } catch (err) {
      console.error('Error downloading report:', err);
    }
  };

  // Coverage percentage
  const coveragePercent = coverageData && coverageData.totalAssets > 0
    ? Math.round((coverageData.inspectedCount / coverageData.totalAssets) * 100)
    : 0;

  return (
    <DisciplineAccessControl requiredDiscipline="Geotechnical">
      <Box sx={{ padding: 3, maxWidth: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Slope Inspection Reports
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage slope inspection reports generated from MapView
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search reports..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                width: { xs: '100%', sm: '240px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
              background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.05)}, ${alpha(theme.palette.success.light, 0.02)})`
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: 48, height: 48 }}>
                  <TerrainIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats.loading ? <CircularProgress size={20} /> : stats.totalReports}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Slope Reports</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
              background: `linear-gradient(45deg, ${alpha(theme.palette.warning.main, 0.05)}, ${alpha(theme.palette.warning.light, 0.02)})`
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 48, height: 48 }}>
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                    {stats.loading ? <CircularProgress size={20} /> : (
                      stats.latestReportDate ? new Date(stats.latestReportDate).toLocaleDateString() : 'N/A'
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Latest Report Date</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Inspection Coverage Tracker */}
        <Paper sx={{ p: 3, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AssessmentIcon sx={{ color: theme.palette.info.main }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Inspection Coverage Tracker
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Check inspection coverage for slopes by type, cycle, and year
          </Typography>

          {/* Filter Dropdowns */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Inspection Type</InputLabel>
                <Select
                  value={selectedTypeId}
                  onChange={handleTypeChange}
                  label="Inspection Type"
                  sx={{ borderRadius: '8px' }}
                >
                  {inspectionTypes.length === 0 ? (
                    <MenuItem disabled value="">No inspection types available</MenuItem>
                  ) : (
                    inspectionTypes.map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {t.name}
                          {t.is_mandatory && (
                            <Chip label="Mandatory" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }} />
                          )}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={!selectedType}>
                <InputLabel>Cycle</InputLabel>
                <Select
                  value={selectedCycle}
                  onChange={(e) => setSelectedCycle(e.target.value)}
                  label="Cycle"
                  sx={{ borderRadius: '8px' }}
                >
                  {getCycleOptions(selectedType?.cycle_frequency).map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                {selectedType?.cycle_frequency && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 0.5 }}>
                    Frequency: {selectedType.cycle_frequency.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Year"
                  sx={{ borderRadius: '8px' }}
                >
                  {yearOptions.map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Loading */}
          {coverageLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {/* Prompt to select */}
          {!coverageData && !coverageLoading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <DonutSmallIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                Select an inspection type, cycle, and year to view coverage
              </Typography>
            </Box>
          )}

          {/* Results */}
          {coverageData && !coverageLoading && (
            <>
              {/* Coverage Stat Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Total Slopes */}
                <Grid item xs={6} md={3}>
                  <Card sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.06)}, ${alpha(theme.palette.info.light, 0.02)})`
                  }}>
                    <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>Total Slopes</Typography>
                      <Typography variant="h4" fontWeight={700} color="info.main">
                        {coverageData.totalAssets}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Inspected */}
                <Grid item xs={6} md={3}>
                  <Card sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.06)}, ${alpha(theme.palette.success.light, 0.02)})`
                  }}>
                    <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>Inspected</Typography>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {coverageData.inspectedCount}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Uninspected */}
                <Grid item xs={6} md={3}>
                  <Card sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.06)}, ${alpha(theme.palette.warning.light, 0.02)})`
                  }}>
                    <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>Uninspected</Typography>
                      <Typography variant="h4" fontWeight={700} color="warning.main">
                        {coverageData.uninspectedCount}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Coverage % */}
                <Grid item xs={6} md={3}>
                  <Card sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.light, 0.02)})`
                  }}>
                    <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>Coverage</Typography>
                      <Typography variant="h4" fontWeight={700} color="primary.main">
                        {coveragePercent}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={coveragePercent}
                        sx={{
                          mt: 0.5,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          '& .MuiLinearProgress-bar': { borderRadius: 3 }
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Status Breakdown */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Inspection Status Breakdown
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(coverageData.statusBreakdown).map(([status, count]) => {
                    const config = statusConfig[status] || { label: status, color: '#9e9e9e' };
                    return (
                      <Chip
                        key={status}
                        label={`${config.label}: ${count}`}
                        size="small"
                        icon={
                          status === 'rated'
                            ? <CheckCircleIcon sx={{ fontSize: 16 }} />
                            : status === 'draft'
                            ? <DescriptionIcon sx={{ fontSize: 16 }} />
                            : undefined
                        }
                        sx={{
                          backgroundColor: alpha(config.color, 0.1),
                          color: config.color,
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          borderRadius: '8px',
                          border: `1px solid ${alpha(config.color, 0.2)}`,
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Duplicate Warnings */}
              {coverageData.duplicates && coverageData.duplicates.length > 0 && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{ mt: 2, borderRadius: '8px' }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {coverageData.duplicates.length} asset{coverageData.duplicates.length > 1 ? 's' : ''} with duplicate inspections
                  </Typography>
                  {coverageData.duplicates.map(dup => (
                    <Typography key={dup.asset_id} variant="body2">
                      {dup.asset_name || 'Unknown'} ({dup.asset_code || dup.asset_id}) &mdash; {dup.count} inspections
                    </Typography>
                  ))}
                </Alert>
              )}
            </>
          )}
        </Paper>

        {/* Reports Table */}
        <Paper sx={{ p: 3, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Slope Inspection Reports
            </Typography>
          </Box>

          <TableContainer sx={{ borderRadius: '10px', boxShadow: 'none', border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.success.main, 0.04) }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Report Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Asset ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Asset Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Generated</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Format</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Generated By</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingReports ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredReports.map((report) => {
                  return (
                    <TableRow
                      key={report.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.success.main, 0.04),
                        },
                        '& .MuiTableCell-root': {
                          py: 1.5,
                          borderColor: alpha(theme.palette.divider, 0.08)
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PictureAsPdfIcon sx={{ mr: 1.5, color: theme.palette.error.main }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {report.report_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={report.asset_id || 'N/A'}
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                            fontSize: '0.75rem',
                            borderRadius: '6px',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {report.asset_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {new Date(report.created_at).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(report.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="PDF"
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.grey[500], 0.1),
                            color: theme.palette.text.primary,
                            fontSize: '0.75rem',
                            borderRadius: '6px',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{report.generated_by_name || 'Unknown'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Tooltip title="View Report">
                            <IconButton
                              size="small"
                              onClick={() => handleViewReport(report)}
                              sx={{
                                color: theme.palette.success.main,
                                backgroundColor: alpha(theme.palette.success.main, 0.08),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.success.main, 0.15),
                                }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadReport(report)}
                              sx={{
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                }
                              }}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Share">
                            <IconButton
                              size="small"
                              sx={{
                                color: theme.palette.info.main,
                                backgroundColor: alpha(theme.palette.info.main, 0.08),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.info.main, 0.15),
                                }
                              }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loadingReports && filteredReports.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <TerrainIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography color="text.secondary">
                          No slope inspection reports found. Generate reports from the MapView inspection modal.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </DisciplineAccessControl>
  );
};

export default SlopeReportsPage;
